import { useState } from 'react';
import styled from '@emotion/styled';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { useIsMobile } from '@stores/AppCommonData';
import { mediaQuery } from '@styles/index';
import NtaWarningModal from '../components/NtaWarningModal';
import { NTA_ALERTS, NTA_COLORS, NTA_TNC_TEXT } from '../constants';
import { ntaBodyCss, ntaButtonCss, ntaPageCss } from '../typography';

const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 0 16px;
  box-sizing: border-box;
  ${ntaPageCss}
`;

const CheckboxRow = styled.label`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  width: 100%;
  padding: 24px 24px 0 2px;
  cursor: pointer;
  position: relative;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  left: 0;
  top: 26px;
  width: 24px;
  height: 20px;
  margin: 0;
  opacity: 0;
  cursor: pointer;
`;

const CheckboxBox = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 20px;
  margin-top: 2px;
  border-radius: 4px;
  border: 1px solid ${NTA_COLORS.checkboxBorder};
  background: ${({ $checked }) =>
    $checked ? NTA_COLORS.proceed : NTA_COLORS.checkboxBg};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &::after {
    content: '';
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
    width: 5px;
    height: 9px;
    border: solid ${NTA_COLORS.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translateY(-1px);
  }
`;

const TncText = styled.span`
  flex: 1;
  color: ${NTA_COLORS.textSecondary};
  ${ntaBodyCss}
`;

const ProceedButton = styled.button`
  width: 370px;
  max-width: 100%;
  height: 48px;
  border: none;
  background: ${({ $disabled }) =>
    $disabled ? NTA_COLORS.checkboxBg : NTA_COLORS.proceed};
  color: ${NTA_COLORS.white};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  ${ntaButtonCss}

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const MobileNote = styled.p`
  margin: 0;
  width: 100%;
  text-align: center;
  color: ${NTA_COLORS.textSecondary};
  ${ntaBodyCss}
`;

const InstructionsFooter = ({
  onProceed,
  isTestStartDisabled = false,
  isPendingAction = false,
  tncText = NTA_TNC_TEXT
}) => {
  const isMobile = useIsMobile();
  const [isChecked, setIsChecked] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const generatedAppLink = useOneLinkSmartScriptV2({
    campaign: 'quiz page'
  });

  if (isMobile) {
    return (
      <>
        <OneLinkSmartScript />
        <Footer>
          <MobileNote>Test can be taken only on the app</MobileNote>
          <ProceedButton
            type="button"
            onClick={() => window.open(generatedAppLink, '_blank')}
          >
            Open In App
          </ProceedButton>
        </Footer>
      </>
    );
  }

  if (isTestStartDisabled) {
    return null;
  }

  const handleProceed = () => {
    if (isPendingAction) return;

    if (!isChecked) {
      setShowWarning(true);
      return;
    }

    onProceed?.();
  };

  return (
    <>
      <Footer>
        <CheckboxRow>
          <HiddenCheckbox
            type="checkbox"
            checked={isChecked}
            onChange={() => setIsChecked((v) => !v)}
          />
          <CheckboxBox $checked={isChecked} aria-hidden />
          <TncText>{tncText || NTA_TNC_TEXT}</TncText>
        </CheckboxRow>
        <ProceedButton
          type="button"
          onClick={handleProceed}
          disabled={isPendingAction}
          $disabled={isPendingAction}
        >
          Proceed
        </ProceedButton>
      </Footer>
      <NtaWarningModal
        open={showWarning}
        title={NTA_ALERTS.WARNING_TITLE}
        message={NTA_ALERTS.ACCEPT_DECLARATION}
        onClose={() => setShowWarning(false)}
      />
    </>
  );
};

export default InstructionsFooter;
